import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  BookOpen,
  BarChart3,
  Eye
} from 'lucide-react';
import api from '@/lib/api';
import { renderLatex } from '@/lib/latex';
import TestCreationProgress from '@/components/tests/TestCreationProgress';

interface Option {
  text: string;
  isCorrect?: boolean;
  optionLetter?: string;
  optionType?: string;
  imageUrl?: string;
  optionIndex?: number;
}

interface Question {
  id: string;
  questionText: string;
  questionImageUrl?: string;
  type: string;
  options: (string | Option)[];
  correctAnswer: string;
  explanation?: string;
  explanationImageUrl?: string;
  subject: string;
  topic: string;
  difficultyLevel: string;
  kitId?: string;
  sequenceNumber?: number; // Global sequence number for display
  subjectSequenceNumber?: number; // Sequence within subject
}

const QuestionPreviewPage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSubject, setActiveSubject] = useState<string>('');
  const [activeTopic, setActiveTopic] = useState<string>('');

  // Get state from navigation
  const testTitle = location.state?.testTitle || 'Test';
  const mode = location.state?.mode || 'view';

  // Helper function to get option image URL
  const getOptionImageUrl = (option: string | Option): string | null => {
    if (typeof option === 'string') {
      return null;
    }
    return option?.imageUrl || null;
  };

  useEffect(() => {
    if (testId) {
      loadQuestions();
    }
  }, [testId]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading questions for test:', testId);

      // Fetch both questions and sequenced questions
      const [questionsResponse, sequencedResponse] = await Promise.all([
        api.get(`/admin/tests/actual/${testId}/questions`),
        api.get(`/admin/tests/actual/${testId}/sequenced-questions`).catch(() => null) // Don't fail if sequenced questions don't exist
      ]);

      const loadedQuestions = questionsResponse.data || [];

      // If sequenced questions are available, use them to enhance question data
      if (sequencedResponse && sequencedResponse.data) {
        const sequencedData = sequencedResponse.data;

        // Create a map of question ID to sequence info
        const sequenceMap = new Map();
        sequencedData.forEach((sq: any) => {
          sequenceMap.set(sq.questionId, {
            sequenceNumber: sq.sequenceNumber,
            subjectSequenceNumber: sq.subjectSequenceNumber
          });
        });

        // Enhance questions with sequence numbers
        const enhancedQuestions = loadedQuestions.map((question: Question) => ({
          ...question,
          sequenceNumber: sequenceMap.get(question.id)?.sequenceNumber,
          subjectSequenceNumber: sequenceMap.get(question.id)?.subjectSequenceNumber
        }));

        setQuestions(enhancedQuestions);
      } else {
        setQuestions(loadedQuestions);
      }
    } catch (err) {
      console.error('Failed to load questions:', err);
      setError('Failed to load test questions');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmTest = () => {
    // Navigate to scheduling step instead of directly creating the test
    navigate(`/tests/schedule/${testId}`, {
      state: {
        testTitle: testTitle,
        mode: 'create'
      }
    });
  };

  const handleBackToEdit = () => {
    navigate('/tests/create');
  };

  const handleClose = () => {
    navigate('/tests');
  };

  // Helper function to extract option text
  const getOptionText = (option: any): string => {
    if (typeof option === 'string') {
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(option);
        if (parsed && typeof parsed === 'object') {
          // Handle parsed JSON object with text property
          if (parsed.text) {
            return parsed.text;
          }
          // Handle other possible structures
          if (parsed.optionText) {
            return parsed.optionText;
          }
          // If it's an object but no text property, return the original string
          return option;
        }
      } catch (e) {
        // Not valid JSON, return as is
        return option;
      }
      return option;
    }
    if (typeof option === 'object' && option !== null) {
      // Handle JSON object with text property
      if (option.text) {
        return option.text;
      }
      // Handle other possible structures
      if (option.optionText) {
        return option.optionText;
      }
      // If it's an object but no text property, stringify it as fallback
      return JSON.stringify(option);
    }
    return option?.toString() || '';
  };

  // Use shared LaTeX rendering function

  // Calculate statistics
  const getStatistics = () => {
    if (questions.length === 0) return null;

    const difficultyCount = questions.reduce((acc, q) => {
      acc[q.difficultyLevel] = (acc[q.difficultyLevel] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const subjectCount = questions.reduce((acc, q) => {
      acc[q.subject] = (acc[q.subject] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topicCount = questions.reduce((acc, q) => {
      acc[q.topic] = (acc[q.topic] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: questions.length,
      difficulty: difficultyCount,
      subjects: subjectCount,
      topics: topicCount
    };
  };

  const statistics = getStatistics();

  // Group questions by subject and topic
  const getGroupedQuestions = () => {
    const grouped: Record<string, Record<string, Question[]>> = {};

    questions.forEach(question => {
      const subject = question.subject || 'Unknown Subject';
      const topic = question.topic || 'Unknown Topic';

      if (!grouped[subject]) {
        grouped[subject] = {};
      }
      if (!grouped[subject][topic]) {
        grouped[subject][topic] = [];
      }

      grouped[subject][topic].push(question);
    });

    return grouped;
  };

  const groupedQuestions = getGroupedQuestions();
  const subjects = Object.keys(groupedQuestions);

  // Initialize active subject and topic when questions load
  useEffect(() => {
    if (questions.length > 0 && activeSubject === '' && activeTopic === '' && subjects.length > 0) {
      // Already defaulted to "All Subjects" and "All Topics" - no need to change
      return;
    }
    if (questions.length > 0 && !activeSubject && !activeTopic) {
      // Default to "All Subjects" and "All Topics"
      setActiveSubject('');
      setActiveTopic('');
    }
  }, [questions.length, activeSubject, activeTopic, subjects.length]);

  // Get topics for active subject
  const activeSubjectTopics = activeSubject ? Object.keys(groupedQuestions[activeSubject] || {}) : [];

  // Get questions for active subject and topic
  const getFilteredQuestions = () => {
    // If "All Subjects" is selected (activeSubject is empty)
    if (activeSubject === '') {
      // If "All Topics" is also selected (activeTopic is empty), show all questions in sequence
      if (activeTopic === '') {
        // Sort questions by their sequence number to maintain proper order
        return questions.sort((a, b) => {
          const seqA = a.sequenceNumber || 0;
          const seqB = b.sequenceNumber || 0;
          return seqA - seqB;
        });
      }
      // If a specific topic is selected under "All Subjects", show questions from that topic across all subjects
      return questions.filter(q => q.topic === activeTopic);
    }

    // If a specific subject is selected
    if (activeSubject) {
      // If "All Topics" is selected for this subject, show all questions from this subject
      if (activeTopic === '') {
        return questions.filter(q => q.subject === activeSubject).sort((a, b) => {
          const seqA = a.sequenceNumber || 0;
          const seqB = b.sequenceNumber || 0;
          return seqA - seqB;
        });
      }
      // If a specific topic is selected, show questions from this subject and topic
      return groupedQuestions[activeSubject]?.[activeTopic] || [];
    }

    // Default fallback
    return questions;
  };

  const filteredQuestions = getFilteredQuestions();

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy': return 'bg-green-100 text-green-800 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'hard': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div>
      {/* Progress Indicator - Only show during creation flow */}
      {mode === 'create' && (
        <TestCreationProgress currentStep="preview" testTitle={testTitle} />
      )}

      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Tests
          </Button>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <Eye className="h-6 w-6" />
          <h1 className="text-3xl font-bold">
            {mode === 'create' ? 'Question Preview' : 'Test Questions'} - {testTitle}
          </h1>
        </div>
        <p className="text-muted-foreground">
          {mode === 'create'
            ? 'Review the selected questions before finalizing the test'
            : 'Preview all questions in this test'
          }
        </p>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading questions...</p>
          </div>
        </div>
      ) : error ? (
        <Alert variant="destructive" className="mb-6">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button onClick={loadQuestions} className="ml-4" size="sm">
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-6">
          {/* Compact Statistics Card */}
          <div className="mb-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-primary" />
                  <span className="font-medium text-sm">Test Statistics</span>
                </div>
                <div className="text-lg font-bold text-primary">{statistics?.total || 0} Questions</div>
              </div>

              <div className="flex items-center gap-4 text-xs flex-wrap">
                {/* Difficulty Distribution */}
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Difficulty:</span>
                  {Object.entries(statistics?.difficulty || {}).map(([difficulty, count]) => (
                    <Badge key={difficulty} className={`${getDifficultyColor(difficulty)} text-xs px-2 py-0`}>
                      {difficulty.charAt(0)}: {count}
                    </Badge>
                  ))}
                </div>

                {/* Subject Count */}
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Subjects:</span>
                  <Badge variant="outline" className="text-xs px-2 py-0">
                    {Object.keys(statistics?.subjects || {}).length}
                  </Badge>
                </div>

                {/* Topic Count */}
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Topics:</span>
                  <Badge variant="outline" className="text-xs px-2 py-0">
                    {Object.keys(statistics?.topics || {}).length}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Subject Tabs */}
          {subjects.length > 0 && (
            <div className="space-y-4">
              {/* Subject Pills */}
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                <h3 className="text-xs font-medium text-gray-700 mr-3 flex items-center">
                  <BookOpen className="h-3 w-3 mr-1" />
                  Subjects:
                </h3>

                {/* All Subjects pill */}
                <button
                  onClick={() => {
                    setActiveSubject('');
                    setActiveTopic('');
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                    activeSubject === ''
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  All Subjects
                  <span className={`text-xs px-1.5 py-0 rounded-full ${
                    activeSubject === '' ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {questions.length}
                  </span>
                </button>

                {subjects.map((subject) => {
                  const subjectQuestionCount = Object.values(groupedQuestions[subject] || {})
                    .reduce((total, topicQuestions) => total + topicQuestions.length, 0);

                  return (
                    <button
                      key={subject}
                      onClick={() => {
                        setActiveSubject(subject);
                        const topics = Object.keys(groupedQuestions[subject] || {});
                        if (topics.length > 0) {
                          setActiveTopic(topics[0]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                        activeSubject === subject
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {subject}
                      <span className={`text-xs px-1.5 py-0 rounded-full ${
                        activeSubject === subject ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {subjectQuestionCount}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Topic Pills */}
              {(activeSubject === '' || (activeSubject && activeSubjectTopics.length > 0)) && (
                <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg">
                  <h3 className="text-xs font-medium text-blue-700 mr-3 flex items-center">
                    📚 {activeSubject === '' ? 'Topics:' : `Topics in ${activeSubject}:`}
                  </h3>

                  {/* All Topics pill - show when All Subjects is selected or when a specific subject is selected */}
                  <button
                    onClick={() => setActiveTopic('')}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                      activeTopic === ''
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    All Topics
                    <span className={`text-xs px-1.5 py-0 rounded-full ${
                      activeTopic === '' ? 'bg-white/20 text-white' : 'bg-blue-200 text-blue-700'
                    }`}>
                      {activeSubject === '' ? questions.length : Object.values(groupedQuestions[activeSubject] || {}).reduce((total, topicQuestions) => total + topicQuestions.length, 0)}
                    </span>
                  </button>

                  {/* Individual topic pills - only show when a specific subject is selected */}
                  {activeSubject !== '' && activeSubjectTopics.map((topic) => {
                    const topicQuestionCount = groupedQuestions[activeSubject]?.[topic]?.length || 0;

                    return (
                      <button
                        key={topic}
                        onClick={() => setActiveTopic(topic)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                          activeTopic === topic
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-blue-700 hover:bg-blue-100'
                        }`}
                      >
                        {topic}
                        <span className={`text-xs px-1.5 py-0 rounded-full ${
                          activeTopic === topic ? 'bg-white/20 text-white' : 'bg-blue-200 text-blue-700'
                        }`}>
                          {topicQuestionCount}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Questions for Active Subject/Topic */}
              <div className="space-y-4">
                {filteredQuestions.map((question, questionIndex) => {
                  // Use sequence number if available, otherwise fall back to index
                  const displayNumber = question.sequenceNumber || (questionIndex + 1);

                  return (
                    <div key={question.id} className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-base font-semibold text-primary">
                          Q{displayNumber}
                        </h3>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${getDifficultyColor(question.difficultyLevel)}`}>
                            {question.difficultyLevel.charAt(0)}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 flex items-center gap-1">
                            <BookOpen className="h-3 w-3" />
                            {question.subject}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {/* Question Text */}
                        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                          <div className="break-words overflow-wrap-anywhere whitespace-pre-wrap leading-relaxed text-base font-medium text-gray-900">
                            {renderLatex(question.questionText)}
                          </div>
                          {/* Question Image */}
                          {question.questionImageUrl && (
                            <div className="mt-4">
                              <img
                                src={`${window.location.origin}/images/${question.questionImageUrl}`}
                                alt="Question"
                                className="max-w-full h-auto rounded-lg shadow-sm"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  console.error('Failed to load question image:', `${window.location.origin}/images/${question.questionImageUrl}`);
                                  target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>

                        {/* Options */}
                        <div className="grid grid-cols-1 gap-2">
                          {question.options.map((option, optionIndex) => {
                            const optionText = getOptionText(option);
                            const optionImageUrl = getOptionImageUrl(option);
                            const optionLetter = String.fromCharCode(65 + optionIndex);
                            const isCorrect = question.correctAnswer === optionLetter ||
                                             question.correctAnswer === optionText ||
                                             question.correctAnswer === option;

                            return (
                              <div
                                key={optionIndex}
                                className={`p-2.5 rounded-lg transition-colors ${
                                  isCorrect
                                    ? 'bg-green-50 text-green-800'
                                    : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                              >
                                <div className="flex items-start gap-2.5">
                                  {isCorrect && (
                                    <CheckCircle className="h-3.5 w-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                                  )}
                                  <span className="font-medium flex-shrink-0 min-w-[20px] text-sm">{optionLetter}.</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="break-words overflow-wrap-anywhere whitespace-pre-wrap leading-relaxed text-sm">
                                      {renderLatex(optionText)}
                                    </div>
                                    {/* Option Image */}
                                    {optionImageUrl && (
                                      <div className="mt-2">
                                        <img
                                          src={`${window.location.origin}/images/${optionImageUrl}`}
                                          alt={`Option ${optionLetter}`}
                                          className="max-w-full h-auto rounded-md shadow-sm"
                                          onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            console.error('Failed to load option image:', `${window.location.origin}/images/${optionImageUrl}`);
                                            target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Question Metadata */}
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                          <span>Topic:</span>
                          <span className="px-2 py-1 bg-gray-100 rounded-full">{question.topic}</span>
                          <span>Type:</span>
                          <span className="px-2 py-1 bg-gray-100 rounded-full">{question.type}</span>
                          {question.kitId && (
                            <>
                              <span>Kit:</span>
                              <span className="px-2 py-1 bg-gray-100 rounded-full">{question.kitId}</span>
                            </>
                          )}
                        </div>

                        {question.explanation && (
                          <div className="p-2.5 bg-blue-50 rounded-lg text-xs">
                            <div className="break-words overflow-wrap-anywhere whitespace-pre-wrap leading-relaxed">
                              <span className="font-medium text-blue-800">Explanation: </span>
                              {renderLatex(question.explanation)}
                            </div>
                            {/* Explanation Image */}
                            {question.explanationImageUrl && (
                              <div className="mt-3">
                                <img
                                  src={`${window.location.origin}/images/${question.explanationImageUrl}`}
                                  alt="Explanation"
                                  className="max-w-full h-auto rounded-lg shadow-sm"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    console.error('Failed to load explanation image:', `${window.location.origin}/images/${question.explanationImageUrl}`);
                                    target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer Actions */}
      <div className="mt-8 p-4 border-t bg-white sticky bottom-0">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="text-sm text-muted-foreground">
            {questions.length > 0 && `${questions.length} questions ${mode === 'create' ? 'selected' : 'in test'}`}
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            {mode === 'create' && (
              <Button variant="outline" onClick={handleBackToEdit} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Edit
              </Button>
            )}
            <Button variant="outline" onClick={handleClose}>
              {mode === 'create' ? 'Cancel' : 'Close'}
            </Button>
            {mode === 'create' && (
              <Button
                onClick={handleConfirmTest}
                disabled={loading || questions.length === 0}
                className="flex items-center gap-2"
              >
                <ArrowRight className="h-4 w-4" />
                Continue to Scheduling
              </Button>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default QuestionPreviewPage;
